import { Fragment, type ReactNode } from "react";
import type { PolicyBlock, PolicyLink } from "@/data/legacy-policies";

const linkClass = "text-moss underline decoration-moss/30 underline-offset-4 hover:decoration-moss";

/** Wraps the first occurrence of each link's text in an anchor, leaving the wording untouched. */
function withLinks(v: string, links?: PolicyLink[]): ReactNode {
  if (!links?.length) return v;
  const parts: ReactNode[] = [];
  let rest = v;
  links.forEach((link, i) => {
    const at = rest.indexOf(link.text);
    if (at < 0) return;
    parts.push(rest.slice(0, at));
    const external = link.href.startsWith("http");
    parts.push(
      <a
        key={i}
        href={link.href}
        className={linkClass}
        {...(external ? { target: "_blank", rel: "nofollow noopener noreferrer" } : {})}
      >
        {link.text}
      </a>,
    );
    rest = rest.slice(at + link.text.length);
  });
  parts.push(rest);
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}

export function LegacyPolicyBody({ blocks }: { blocks: PolicyBlock[] }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.t) {
          case "h1":
            return <h1 key={i} className="max-w-3xl pt-5 font-serif text-4xl text-charcoal md:text-5xl">{b.v}</h1>;
          case "h3":
            return <h3 key={i} className="pt-5 font-serif text-2xl text-charcoal md:text-3xl">{b.v}</h3>;
          case "h4":
            return <h4 key={i} className="pt-5 font-serif text-xl text-charcoal md:text-2xl">{b.v}</h4>;
          case "h6":
            return <h6 key={i} className="pt-5 font-serif text-xl text-charcoal md:text-2xl">{b.v}</h6>;
          case "ul":
            return <ul key={i} className="list-disc space-y-2 pl-5 marker:text-moss">{b.items.map((it, j) => <li key={j}>{withLinks(it.v, it.links)}</li>)}</ul>;
          case "ol":
            return <ol key={i} className="list-decimal space-y-2 pl-5 marker:text-moss">{b.items.map((it, j) => <li key={j}>{withLinks(it.v, it.links)}</li>)}</ol>;
          default:
            return <p key={i}>{withLinks(b.v, b.links)}</p>;
        }
      })}
    </>
  );
}
